import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Droplet, Thermometer, Home as HomeIcon, Bath, Wrench, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services, testimonials, faqs, campaigns, contactInfo } from '../mockData';

const Home = () => {
  const getIcon = (iconName) => {
    const icons = {
      droplet: Droplet,
      thermometer: Thermometer,
      home: HomeIcon,
      bath: Bath,
      pipe: Wrench,
      utensils: Wrench
    };
    const IconComponent = icons[iconName] || Wrench;
    return <IconComponent className="w-8 h-8 text-yellow-500" />;
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            {campaigns.filter(c => c.active).length > 0 && (
              <div className="inline-block px-6 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
                <p className="text-yellow-500 text-sm font-medium">{campaigns[0].title}</p>
              </div>
            )}
            <h1 className="hero-title">
              TÜM SU KAÇAKLARINA
              <br />
              <span className="text-gradient-gold">KESİN ÇÖZÜM!</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Bayraklı ve Tüm İzmir'de Kırmadan Su Kaçağı Tespiti, Kanal Açma ve Tesisat Çözümleri
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
              <a href={`tel:${contactInfo.phones[0].number.replace(/\s/g, '')}`}>
                <Button size="lg" className="bg-yellow-500 text-black hover:bg-yellow-600 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Su Kaçağı için Hemen Ara
                </Button>
              </a>
              <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black text-lg px-8 py-6">
                  WhatsApp'tan Fotoğraf Gönder
                </Button>
              </a>
            </div>
            <div className="pt-8 flex flex-wrap gap-6 justify-center text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-300">Kırmadan Tespit</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-300">Aynı Gün Çözüm</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-300">Profesyonel Ekipman</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gradient-to-b from-black to-zinc-950">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title text-gradient-gold">Hizmetlerimiz</h2>
            <p className="section-subtitle">
              İzmir'de tesisat ve tadilat alanında profesyonel çözümler sunuyoruz
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-zinc-900 border-zinc-800 card-hover overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <CardHeader>
                  <div className="service-icon mx-auto">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">{service.title}</h3>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p className="text-gray-400">{service.shortDesc}</p>
                  <Link to={`/hizmet/${service.id}`}>
                    <Button variant="outline" className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black">
                      Detaylı Bilgi
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-zinc-950">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title text-gradient-gold">Neden EMRE TİCARET?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Kırmadan Tespit', desc: 'Duvarlarınızı kırmadan kaçak tespiti' },
              { title: 'Profesyonel Ekipman', desc: 'En son teknoloji cihazlarla hizmet' },
              { title: 'Aynı Gün Çözüm', desc: 'Acil durumlarda hızlı müdahale' },
              { title: 'Garantili İşçilik', desc: 'Tüm işlerimizde garanti belgesi' }
            ].map((item, index) => (
              <div key={index} className="text-center space-y-3">
                <div className="w-16 h-16 mx-auto bg-yellow-500/10 rounded-full flex items-center justify-center border-2 border-yellow-500/30">
                  <CheckCircle className="w-8 h-8 text-yellow-500" />
                </div>
                <h3 className="text-lg font-bold text-white">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title text-gradient-gold">Müşteri Yorumları</h2>
            <p className="section-subtitle">İzmir'deki müşterilerimizin görüşleri</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card space-y-4">
                <div className="rating-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 text-sm italic">"{testimonial.comment}"</p>
                <div className="pt-4 border-t border-zinc-800">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-xs text-gray-500">{testimonial.location}</p>
                  <p className="text-xs text-yellow-500 mt-1">{testimonial.service}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/yorumlar">
              <Button variant="outline" className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black">
                Tüm Yorumları Gör
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-zinc-950">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title text-gradient-gold">Sıkça Sorulan Sorular</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-zinc-900 border-zinc-800 rounded-lg px-6">
                  <AccordionTrigger className="text-white hover:text-yellow-500">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-zinc-900 via-black to-zinc-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center space-y-6 py-12">
            <h2 className="text-4xl font-bold text-white">
              Bize Sormadan <span className="text-gradient-gold">Evinizi Kırdırmayın!</span>
            </h2>
            <p className="text-xl text-gray-300">
              Profesyonel ekipman ve deneyimli kadromuzla hizmetinizdeyiz
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <a href={`tel:${contactInfo.phones[0].number.replace(/\s/g, '')}`}>
                <Button size="lg" className="bg-yellow-500 text-black hover:bg-yellow-600 px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  {contactInfo.phones[0].number}
                </Button>
              </a>
              <Link to="/iletisim">
                <Button size="lg" variant="outline" className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black">
                  İletişim Formu
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
